import { 
  HomelabStatus, 
  ChatMessage, 
  UserProfile, 
  AuthUser, 
  AuthSession, 
  ChatAttachment,
  CameraInfo,
  PtzCommandParams,
  CameraControlResponse,
  PtzDirection,
  PtzAction,
  HomeDevice,
  FrigateEvent,
  NetworkDiagnosticsReport,
  TriggeredAlertPayload
} from '../types';
import { dispatchEdgeInference, getEdgeExecutionMode } from './edgeModelAdapter';
import { connectionManager, PRIMARY_LAN_URL, FALLBACK_TAILSCALE_URL, PUBLIC_RELAY_URL } from './connectionManager';
import { getUsbDeviceStatus, getUsbLogs, sendAndListenUsbSerial } from './usbSerial';
import { checkShizuku, execShizuku } from './shizuku';

export type { CameraInfo, PtzCommandParams, CameraControlResponse, PtzDirection, PtzAction };

const STORAGE_KEY_AUTH_TOKEN = 'fissal_auth_token';
const STORAGE_KEY_AUTH_USER = 'fissal_auth_user';

export const DEFAULT_LAN_URL = PRIMARY_LAN_URL;
export const DEFAULT_TAILSCALE_URL = FALLBACK_TAILSCALE_URL;
export const DEFAULT_PUBLIC_URL = PUBLIC_RELAY_URL;

export interface FamilyNote {
  id: number;
  timestamp: string;
  sender: string;
  text: string;
}

export interface RoutingProvider {
  id: 'gemini' | 'openrouter' | 'ollama' | 'hybrid';
  name: string;
  description: string;
}

export interface RoutingConfig {
  provider: 'gemini' | 'openrouter' | 'ollama' | 'hybrid';
  gemini_model: string;
  openrouter_model: string;
  ollama_model: string;
}

export interface RoutingInfo {
  ok: boolean;
  current: RoutingConfig;
  hasGeminiKey: boolean;
  hasOpenRouterKey: boolean;
  availableProviders: RoutingProvider[];
  geminiModels: string[];
}

export function getServerUrl(): string {
  return connectionManager.getActiveUrl();
}

export function setServerUrl(url: string) {
  connectionManager.setPresetMode('CUSTOM', url);
}

export function getStoredToken(): string | null {
  return localStorage.getItem(STORAGE_KEY_AUTH_TOKEN);
}

export function setStoredToken(token: string | null) {
  if (token) localStorage.setItem(STORAGE_KEY_AUTH_TOKEN, token);
  else localStorage.removeItem(STORAGE_KEY_AUTH_TOKEN);
}

export function getStoredAuthUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_AUTH_USER);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setStoredAuthUser(user: AuthUser | null) {
  if (user) localStorage.setItem(STORAGE_KEY_AUTH_USER, JSON.stringify(user));
  else localStorage.removeItem(STORAGE_KEY_AUTH_USER);
}

export function setStoredProfile(profile: UserProfile) {
  localStorage.setItem('fissal_profile', profile);
}

export async function hydrateSecureSession(): Promise<{ ok: boolean; token?: string; user?: AuthUser; profile?: UserProfile }> {
  const token = getStoredToken();
  const user = getStoredAuthUser();
  const profile = (localStorage.getItem('fissal_profile') as UserProfile) || 'echo';
  return { ok: !!token, token: token || undefined, user: user || undefined, profile };
}


function getAuthHeaders(): Record<string, string> {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// === SOVEREIGN AUTHENTICATION API ===

export async function loginWithCredentials(
  identifier: string,
  passcode: string
): Promise<{ ok: boolean; session?: AuthSession; error?: string }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ identifier, passcode, pin: passcode }),
      signal: AbortSignal.timeout(6000)
    });
    const data = await res.json();
    if (res.ok && data.ok && data.token) {
      setStoredToken(data.token);
      setStoredAuthUser(data.user);
      return { ok: true, session: data };
    }
    return { ok: false, error: data.error || 'Authentication failed' };
  } catch (err: any) {
    return { ok: false, error: `Connection failed: ${err.message}` };
  }
}

export async function requestEmailOTP(
  email: string
): Promise<{ ok: boolean; message?: string; error?: string }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/auth/otp/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ email }),
      signal: AbortSignal.timeout(5000)
    });
    const data = await res.json();
    if (res.ok && data.ok) {
      return { ok: true, message: data.message };
    }
    return { ok: false, error: data.error || 'Failed to request OTP' };
  } catch (err: any) {
    return { ok: false, error: `Connection error: ${err.message}` };
  }
}

export async function verifyEmailOTP(
  email: string,
  code: string
): Promise<{ ok: boolean; session?: AuthSession; error?: string }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/auth/otp/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ email, code }),
      signal: AbortSignal.timeout(6000)
    });
    const data = await res.json();
    if (res.ok && data.ok && data.token) {
      setStoredToken(data.token);
      setStoredAuthUser(data.user);
      return { ok: true, session: data };
    }
    return { ok: false, error: data.error || 'Invalid OTP code' };
  } catch (err: any) {
    return { ok: false, error: `Connection error: ${err.message}` };
  }
}

export async function identifyLegacy(
  identifier: string,
  type: 'discord' | 'eso' | 'mac' | 'guest' = 'discord'
): Promise<{ ok: boolean; session?: AuthSession; error?: string }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/auth/legacy-identify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ identifier, type }),
      signal: AbortSignal.timeout(6000)
    });
    const data = await res.json();
    if (res.ok && data.ok && data.token) {
      setStoredToken(data.token);
      setStoredAuthUser(data.user);
      return { ok: true, session: data };
    }
    return { ok: false, error: data.error || 'Legacy identification failed' };
  } catch (err: any) {
    return { ok: false, error: `Connection error: ${err.message}` };
  }
}

export async function checkAuthMe(): Promise<{ ok: boolean; user?: AuthUser; expired?: boolean }> {
  const target = getServerUrl();
  const token = getStoredToken();
  if (!token) return { ok: false };

  try {
    const res = await fetch(`${target}/api/auth/me`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...getAuthHeaders()
      },
      signal: AbortSignal.timeout(4000)
    });
    if (res.status === 401 || res.status === 403) {
      return { ok: false, expired: true };
    }
    if (res.ok) {
      const data = await res.json();
      if (data.ok && data.user) {
        setStoredAuthUser(data.user);
        return { ok: true, user: data.user };
      }
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Auth check failed:', err.message);
  }
  return { ok: false };
}


export async function logoutUser(): Promise<void> {
  const target = getServerUrl();
  try {
    await fetch(`${target}/api/auth/logout`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      signal: AbortSignal.timeout(2000)
    });
  } catch {}
  setStoredToken(null);
  setStoredAuthUser(null);
}


export async function loginWithGoogle(
  credentialOrToken: string
): Promise<{ ok: boolean; session?: AuthSession; error?: string }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/auth/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ credential: credentialOrToken, token: credentialOrToken }),
      signal: AbortSignal.timeout(8000)
    });
    const data = await res.json();
    if (res.ok && data.ok && data.token) {
      setStoredToken(data.token);
      setStoredAuthUser(data.user);
      return { ok: true, session: data };
    }
    return { ok: false, error: data.error || 'Google authentication failed' };
  } catch (err: any) {
    return { ok: false, error: `Google Sign-in connection failed: ${err.message}` };
  }
}

export async function fetchFamilyRegistry(): Promise<{ ok: boolean; owner_emails?: string[]; family_emails?: Record<string, any> }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/admin/family-emails`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...getAuthHeaders()
      },
      signal: AbortSignal.timeout(4000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to fetch family registry:', err.message);
  }
  return { ok: false };
}

export async function registerFamilyEmail(
  email: string,
  displayName: string,
  title?: string
): Promise<{ ok: boolean; message?: string; error?: string }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/admin/family-emails`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({ email, displayName, title }),
      signal: AbortSignal.timeout(5000)
    });
    const data = await res.json();
    if (res.ok && data.ok) {
      return { ok: true, message: data.message };
    }
    return { ok: false, error: data.error || 'Failed to register family email' };
  } catch (err: any) {
    return { ok: false, error: err.message };
  }
}

export async function removeFamilyEmail(email: string): Promise<{ ok: boolean; error?: string }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/admin/family-emails/${encodeURIComponent(email)}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...getAuthHeaders()
      },
      signal: AbortSignal.timeout(5000)
    });
    return await res.json();
  } catch (err: any) {
    return { ok: false, error: err.message };
  }
}

// === HOMELAB & CAMERA ENDPOINTS ===

export const FALLBACK_FLEET_CAMERAS: CameraInfo[] = [
  {
    id: 'e-roto_1',
    name: 'Dogshop - Porch PTZ',
    zone: 'Dogshop Porch & Yard',
    location: 'Dogshop Porch & Yard',
    model: 'Tapo TCW30 Outdoor PTZ',
    ip: '192.168.70.103',
    dns: 'dogshop-roto.echo',
    ptz: true,
    light: true,
    speaker: true,
    hasSub: true,
    presets: ['house', 'driveway (renters)', 'driveway (main)', 'dogshop door'],
    status: 'online',
    fps: 3.1,
    light_state: 'off',
    snapshotUrl: '/api/frigate/snapshot/e-roto_1',
    streamUrl: 'https://frigate.ech-o.net/#e-roto_1',
    roles: ['detect', 'record', 'ptz'],
    isBattery: false
  },
  {
    id: 'e-roto_2',
    name: 'Dogshop - Rear PTZ',
    zone: 'Dogshop Rear Yard',
    location: 'Dogshop Rear Yard',
    model: 'Tapo TCW30 Outdoor PTZ',
    ip: '192.168.70.105',
    dns: 'dogshop-roto-2.echo',
    ptz: true,
    light: true,
    speaker: true,
    hasSub: true,
    presets: [],
    status: 'online',
    fps: 3.1,
    light_state: 'off',
    snapshotUrl: '/api/frigate/snapshot/e-roto_2',
    streamUrl: 'https://frigate.ech-o.net/#e-roto_2',
    roles: ['detect', 'record', 'ptz'],
    isBattery: false
  },
  {
    id: 'ipcam_100',
    name: 'Lycan - Front Road & Driveway',
    zone: 'Lycan House (Front Road & Driveway)',
    location: 'Lycan House (Front Road & Driveway)',
    model: 'C6F0SfZ3N0P6L2 (HiSilicon Hipcam)',
    ip: '192.168.70.100',
    dns: 'lycan-road.echo',
    ptz: false,
    light: false,
    speaker: false,
    hasSub: true,
    presets: [],
    status: 'online',
    fps: 4.0,
    light_state: 'off',
    snapshotUrl: '/api/frigate/snapshot/ipcam_100',
    streamUrl: 'https://frigate.ech-o.net/#ipcam_100',
    roles: ['detect', 'record']
  },
  {
    id: 'lycan_yard_echo',
    name: 'Lycan - Yard Towards Echo',
    zone: 'Lycan House (Backyard towards Echo)',
    location: 'Lycan House (Backyard towards Echo)',
    model: 'C6F0SfZ3N0P6L2 (HiSilicon Hipcam)',
    ip: '192.168.70.101',
    dns: 'lycan-yard.echo',
    ptz: false,
    light: false,
    speaker: false,
    hasSub: true,
    presets: [],
    status: 'online',
    fps: 4.0,
    light_state: 'off',
    snapshotUrl: '/api/frigate/snapshot/lycan_yard_echo',
    streamUrl: 'https://frigate.ech-o.net/#lycan_yard_echo',
    roles: ['detect', 'record']
  },
  {
    id: 'e-static_1',
    name: "Lycan - Sharon's Back Porch",
    zone: 'Sharon Back Porch & Dogs',
    location: 'Sharon Back Porch & Dogs',
    model: 'Tapo TCW61 Outdoor (Fixed)',
    ip: '192.168.70.102',
    dns: 'lycan-backporch.echo',
    ptz: false,
    light: true,
    speaker: true,
    hasSub: true,
    presets: [],
    status: 'online',
    fps: 3.1,
    light_state: 'off',
    snapshotUrl: '/api/frigate/snapshot/e-static_1',
    streamUrl: 'https://frigate.ech-o.net/#e-static_1',
    roles: ['detect', 'audio']
  },
  {
    id: 'battery_roto_104',
    name: 'Unconnected Battery Roto Cam',
    zone: 'Sharon Property',
    location: 'Sharon Property',
    model: 'Battery Rotational Camera',
    ip: '192.168.70.104',
    dns: 'battery-roto.echo',
    ptz: true,
    light: false,
    speaker: false,
    hasSub: false,
    presets: [],
    isBattery: true,
    status: 'standby',
    fps: 0,
    light_state: 'off',
    snapshotUrl: '/api/frigate/snapshot/battery_roto_104',
    streamUrl: 'https://frigate.ech-o.net/#battery_roto_104',
    roles: ['standby'],
    note: 'Battery-operated rotational camera; excluded from continuous 24/7 NVR recording'
  }
];

export function getFrigateStreamUrl(cameraId: string, fps: number = 5, height: number = 480): string {
  const target = getServerUrl();
  return `${target}/api/frigate/stream/${cameraId}?fps=${fps}&h=${height}`;
}

export function getFrigateSnapshotUrl(cameraOrEventId: string, cacheBuster?: number): string {
  const target = getServerUrl();
  const cb = cacheBuster || Date.now();
  return `${target}/api/frigate/snapshot/${encodeURIComponent(cameraOrEventId)}?t=${cb}`;
}

export function getFrigateClipUrl(eventId: string): string {
  const target = getServerUrl();
  return `${target}/api/frigate/clip/${encodeURIComponent(eventId)}.mp4`;
}

export function getCameraLiveStreamUrl(cameraId: string, quality: 'sub' | 'main' = 'sub'): string {
  const target = getServerUrl();
  const subEligible = ['e-roto_1', 'e-roto_2', 'ipcam_100', 'lycan_yard_echo', 'e-static_1'];
  const src = (quality === 'sub' && subEligible.includes(cameraId)) ? `${cameraId}_sub` : cameraId;
  return `${target}/live/webrtc/webrtc.html?src=${encodeURIComponent(src)}&media=video`;
}

export async function fetchFrigateCameras(): Promise<CameraInfo[]> {
  const target = getServerUrl();
  const candidateUrls = [
    `${target}/api/v2/frigate/cameras`,
    `${target}/api/cameras`,
    `${target}/api/frigate/cameras`
  ];

  for (const url of candidateUrls) {
    try {
      const res = await fetch(url, {
        method: 'GET',
        signal: AbortSignal.timeout(3500),
        headers: { Accept: 'application/json', ...getAuthHeaders() }
      });
      if (res.ok) {
        const data = await res.json();
        const rawCams: any[] = Array.isArray(data.cameras) ? data.cameras : (Array.isArray(data) ? data : []);
        if (rawCams.length > 0) {
          // Merge with blueprint metadata to guarantee full capabilities
          return FALLBACK_FLEET_CAMERAS.map((def) => {
            const live = rawCams.find((c: any) => c.id === def.id);
            if (!live) return def;
            return {
              ...def,
              ...live,
              isBattery: live.isBattery !== undefined ? live.isBattery : Boolean(def.isBattery),
              presets: Array.isArray(live.presets) && live.presets.length > 0 ? live.presets : def.presets,
              hasSub: live.hasSub !== undefined ? live.hasSub : def.hasSub,
              light: live.light !== undefined ? live.light : def.light,
              speaker: live.speaker !== undefined ? live.speaker : def.speaker,
              ptz: live.ptz !== undefined ? live.ptz : def.ptz,
              status: live.status || def.status,
              fps: typeof live.fps === 'number' ? live.fps : def.fps,
              light_state: live.light_state || def.light_state,
              last_ptz: live.last_ptz || def.last_ptz
            };
          });
        }
      }
    } catch {
      // Continue to next candidate
    }
  }

  return FALLBACK_FLEET_CAMERAS;
}

// === HARDWARE CONTROL ENDPOINTS (PTZ, LIGHT, SIREN, TWO-WAY AUDIO, WAKE) ===

export async function postCameraPtz(
  cameraId: string,
  params: PtzCommandParams
): Promise<CameraControlResponse> {
  const target = getServerUrl();
  const endpoints = [
    `${target}/api/v2/frigate/ptz/${cameraId}`,
    `${target}/api/cameras/${cameraId}/ptz`
  ];

  for (const url of endpoints) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(params),
        signal: AbortSignal.timeout(5000)
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (err: any) {
      console.warn(`[FissalAPI] PTZ dispatch to ${url} failed:`, err.message);
    }
  }
  return { ok: false, camera: cameraId, error: 'Failed to dispatch PTZ command' };
}

export { postCameraPtz as controlCameraPtz };




export async function postCameraLight(
  cameraId: string,
  state: 'on' | 'off'
): Promise<CameraControlResponse> {
  const target = getServerUrl();
  const endpoints = [
    `${target}/api/v2/frigate/light/${cameraId}`,
    `${target}/api/cameras/${cameraId}/light`
  ];

  for (const url of endpoints) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ state }),
        signal: AbortSignal.timeout(8000)
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (err: any) {
      console.warn(`[FissalAPI] Spotlight dispatch to ${url} failed:`, err.message);
    }
  }
  return { ok: false, camera: cameraId, error: 'Failed to toggle spotlight' };
}

export async function postCameraSpeak(
  cameraId: string,
  action: 'siren' | 'alarm' | 'stop' | 'silence' | 'speak',
  text?: string
): Promise<CameraControlResponse> {
  const target = getServerUrl();
  const endpoints = [
    `${target}/api/v2/frigate/speak/${cameraId}`,
    `${target}/api/cameras/${cameraId}/speak`
  ];

  for (const url of endpoints) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ action, text }),
        signal: AbortSignal.timeout(8000)
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (err: any) {
      console.warn(`[FissalAPI] Siren/Audio dispatch to ${url} failed:`, err.message);
    }
  }
  return { ok: false, camera: cameraId, error: 'Failed to dispatch siren / audio command' };
}

export async function postCameraWake(cameraId: string): Promise<CameraControlResponse> {
  if (cameraId !== 'battery_roto_104') {
    console.warn("[FissalAPI] Camera " + cameraId + " is mains-powered; wake probe skipped.");
    return { ok: false, camera: cameraId, error: "Camera " + cameraId + " is mains-powered and does not require wake probes." };
  }
  const target = getServerUrl();
  const endpoints = [
    `${target}/api/v2/frigate/wake/${cameraId}`,
    `${target}/api/cameras/${cameraId}/wake`
  ];

  for (const url of endpoints) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...getAuthHeaders()
        },
        signal: AbortSignal.timeout(6000)
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (err: any) {
      console.warn(`[FissalAPI] Wake probe dispatch to ${url} failed:`, err.message);
    }
  }
  return { ok: false, camera: cameraId, error: 'Failed to dispatch wake probe' };
}

export async function postCameraSyncTime(cameraId: string): Promise<CameraControlResponse> {
  const target = getServerUrl();
  const endpoints = [
    `${target}/api/v2/frigate/sync-time/${cameraId}`,
    `${target}/api/cameras/${cameraId}/sync-time`
  ];

  for (const url of endpoints) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...getAuthHeaders()
        },
        signal: AbortSignal.timeout(8000)
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (err: any) {
      console.warn(`[FissalAPI] Clock sync dispatch to ${url} failed:`, err.message);
    }
  }
  return { ok: false, camera: cameraId, error: 'Failed to synchronize camera clock' };
}


export async function checkServerHealth(customUrl?: string): Promise<{ ok: boolean; latencyMs: number }> {
  const target = (customUrl || getServerUrl()).replace(/\/+$/, '');
  const start = performance.now();
  try {
    const res = await fetch(`${target}/healthz`, {
      method: 'GET',
      signal: AbortSignal.timeout(3500),
      headers: { Accept: 'application/json' }
    });
    const latency = Math.round(performance.now() - start);
    return { ok: res.ok, latencyMs: latency };
  } catch {
    return { ok: false, latencyMs: -1 };
  }
}

export async function fetchRoutingConfig(): Promise<RoutingInfo | null> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/routing`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(4000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to fetch routing config:', err.message);
  }
  return null;
}

export async function updateRoutingConfig(
  config: Partial<RoutingConfig>
): Promise<{ ok: boolean; current?: RoutingConfig; message?: string }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/routing`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(config),
      signal: AbortSignal.timeout(5000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to update routing config:', err.message);
  }
  return { ok: false, message: 'Failed to update routing configuration on server' };
}


export async function fetchStatus(): Promise<HomelabStatus> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/status`, {
      method: 'GET',
      signal: AbortSignal.timeout(4000),
      headers: { Accept: 'application/json', ...getAuthHeaders() }
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data;
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to fetch live server status:', err.message);
    return {
      host: target.includes('100.83') ? '100.83.56.28 (Tailscale :8092)' : '192.168.68.50 (LAN :8092)',
      uptime: 'Online',
      timestamp: new Date().toISOString(),
      persona: {
        driftScore: 0.18,
        driftStatus: 'STABLE & GROUNDED (THE 13 RULE)',
        triad: { azure: 99, verdant: 100, amber: 98 },
        memory: {
          totalAnchors: 21,
          userMemories: 16,
          groundingContexts: 2,
          qdrantPoints: 12,
          qdrantCollections: 4,
          invocations: 945,
          chatLogs: 125
        },
        vitals: {
          gearTemp: 38,
          purrHz: 42.8,
          identity: 'Clockwork Alfiq Sentinel (Fissal Prime)',
          anchor: 'Echo (Master of Keys & Creator 🗝️)',
          post: 'The Relay Command Node',
          philosophy: 'Under 13 Steps (Pocket Watch)'
        },
        thought: 'Infinite gear ticks warmly. Connected via mobile quantum link. Zero amnesia drift detected.'
      },
      services: [
        { id: 6, name: 'fissal-gateway', status: 'online', cpu: 0.1, memory: 24, uptime: '1h', restarts: 0 },
        { id: 5, name: 'fissal-sentinel', status: 'online', cpu: 0.8, memory: 108, uptime: '38h', restarts: 1 },
        { id: 3, name: 'RedfurBot', status: 'online', cpu: 0.6, memory: 622, uptime: '48h', restarts: 0 },
        { id: 0, name: 'frigate-brain', status: 'online', cpu: 0.4, memory: 86, uptime: '48h', restarts: 0 },
        { id: 1, name: 'frigate-web', status: 'online', cpu: 0.4, memory: 85, uptime: '48h', restarts: 0 },
        { id: 2, name: 'homelab-dashboard', status: 'online', cpu: 0.2, memory: 107, uptime: '21h', restarts: 3 },
      ],
      docker: [
        { id: 'c1', name: 'frigate', status: 'Up (healthy)', ports: '5000->5000/tcp', state: 'running' },
        { id: 'c2', name: 'qdrant', status: 'Up', ports: '6333->6333/tcp', state: 'running' },
        { id: 'c3', name: 'immich_server', status: 'Up', ports: '2283->2283/tcp', state: 'running' },
        { id: 'c4', name: 'open-webui-fissal', status: 'Up', ports: '3001->8080/tcp', state: 'running' },
      ],
      system: {
        cpuPercent: 12.4,
        memPercent: 44.2,
        diskPercent: 48.5,
        loadAvg: [0.65, 0.72, 0.80]
      }
    };
  }
}

export async function fetchFamilyNotes(): Promise<FamilyNote[]> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/family/notes`, {
      method: 'GET',
      headers: { Accept: 'application/json', ...getAuthHeaders() },
      signal: AbortSignal.timeout(4000)
    });
    if (res.ok) {
      const data = await res.json();
      return data.notes || [];
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to fetch family notes:', err.message);
  }
  return [];
}

export async function postFamilyNote(note: string, sender: string = 'Mom'): Promise<{ ok: boolean; message: string }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/family/notes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({ note, sender }),
      signal: AbortSignal.timeout(5000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to post family note:', err.message);
  }
  return { ok: false, message: 'Could not connect to server' };
}

export async function sendFissalChat(
  message: string, 
  history: ChatMessage[] = [], 
  profile: UserProfile = 'echo',
  routingOverride?: string,
  attachments?: ChatAttachment[]
): Promise<{ answer: string; tools?: any[]; provider?: string; clientActions?: any[] }> {
  const edgeMode = getEdgeExecutionMode();

  // 1. Air-Gapped Sovereign: Force 100% on-device local execution without network
  if (edgeMode === 'sovereign_offline') {
    const edgeResult = await dispatchEdgeInference(message, history, profile, 'Air-Gapped Sovereign Mode active');
    return {
      answer: edgeResult.answer,
      tools: edgeResult.tools || [],
      provider: edgeResult.provider
    };
  }

  // 2. Edge First (Phone Silicon): If purely conversational, prioritize local silicon
  if (edgeMode === 'edge_first') {
    const lower = message.toLowerCase();
    const isServerToolCmd = [
      'pm2', 'docker', 'camera', 'frigate', 'immich', 'lights', 'tuya',
      'reload', 'container', 'restart', 'fix', 'heal', 'network', 'dns',
      'usb', 'serial', 'shizuku', 'diag', 'status', 'check'
    ].some(k => lower.includes(k));
    if (!isServerToolCmd && (!attachments || attachments.length === 0)) {
      const edgeResult = await dispatchEdgeInference(message, history, profile, 'Edge-First silicon priority active');
      return {
        answer: edgeResult.answer,
        tools: edgeResult.tools || [],
        provider: edgeResult.provider
      };
    }
  }

  const isAdmin = profile === 'echo';
  const queryParam = isAdmin ? '?view=admin' : '?view=family';

  // Gather live phone hardware state (USB-C OTG & Shizuku) with fast non-blocking fallback
  let clientHardware: any = undefined;
  try {
    const [usbStatus, usbLogs, shizukuStatus] = await Promise.all([
      getUsbDeviceStatus().catch(() => null),
      getUsbLogs(20).catch(() => []),
      checkShizuku().catch(() => null)
    ]);
    clientHardware = {
      usb_serial: usbStatus,
      usb_recent_logs: Array.isArray(usbLogs) ? usbLogs : [],
      shizuku: shizukuStatus
    };
  } catch {}

  try {
    const res = await connectionManager.fetchWithFailover(`/api/fissal/chat${queryParam}`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        message,
        history: history.map(h => ({ role: h.role, content: h.content })),
        profile,
        client_hardware: clientHardware,
        ...(routingOverride ? { routing: routingOverride } : {}),
        ...(attachments && attachments.length > 0 ? { attachments } : {})
      }),
      signal: AbortSignal.timeout(35000)
    });

    if (!res.ok) {
      const errText = await res.text();
      let parsedErr = '';
      try {
        parsedErr = JSON.parse(errText).error;
      } catch {}
      throw new Error(parsedErr || `Server returned HTTP ${res.status}: ${res.statusText}`);
    }

    const data = await res.json();
    if (data.ok && data.answer) {
      let finalAnswer = data.answer;
      const clientActions = Array.isArray(data.client_actions) ? data.client_actions : [];

      // Execute any phone-side USB-C OTG or Shizuku actions requested by Fissal Prime
      for (const action of clientActions) {
        if (action.type === 'usb_serial_tx_rx' && action.payload) {
          try {
            const txRes = await sendAndListenUsbSerial(action.payload, action.wait_ms || 1200);
            if (txRes.success) {
              const rxText = (txRes.response || '').trim();
              finalAnswer += `\n\n🔌 **Phone USB-C OTG Serial Executed (\`${action.payload}\`):**\n\`\`\`\n${rxText || '(Transmitted — no immediate RX bytes returned)'}\n\`\`\``;
            } else {
              finalAnswer += `\n\n⚠️ **Phone USB-C Serial Notice:** ${txRes.error || 'USB device not connected yet. Tap "Hook USB Port" to connect.'}`;
            }
          } catch (e: any) {
            finalAnswer += `\n\n⚠️ **Phone USB-C Serial Error:** ${e.message}`;
          }
        } else if (action.type === 'shizuku_exec' && action.command) {
          try {
            const shizRes = await execShizuku(action.command);
            if (shizRes.ok) {
              finalAnswer += `\n\n⚡ **Phone Shizuku Shell (\`${action.command}\`):**\n\`\`\`\n${(shizRes.output || '').trim().slice(0, 1200) || '(Exit 0)'}\n\`\`\``;
            } else {
              finalAnswer += `\n\n⚠️ **Phone Shizuku Error:** ${shizRes.error || `Exit ${shizRes.exitCode}`}`;
            }
          } catch (e: any) {
            finalAnswer += `\n\n⚠️ **Phone Shizuku Exception:** ${e.message}`;
          }
        }
      }

      return {
        answer: finalAnswer,
        tools: data.tools || [],
        provider: data.provider || 'fissal-prime-backend',
        clientActions
      };
    }
    throw new Error(data.error || 'Agent returned an empty reply');
  } catch (err: any) {
    if (edgeMode === 'disabled') {
      throw err;
    }
    console.warn('[FissalAPI] Uplink failed, falling back to local Gemma 4 Edge:', err.message);
    const edgeResult = await dispatchEdgeInference(message, history, profile, err.message);
    return {
      answer: edgeResult.answer,
      tools: edgeResult.tools || [],
      provider: edgeResult.provider
    };
  }
}

export async function dispatchToImmichVault(
  fileData: string,
  fileName: string,
  mimeType: string = 'image/jpeg'
): Promise<{ ok: boolean; message: string; assetId?: string }> {
  try {
    const res = await connectionManager.fetchWithFailover('/api/family/photos/upload', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        fileName,
        mimeType,
        base64Data: fileData
      }),
      signal: AbortSignal.timeout(20000)
    });
    if (res.ok) {
      const data = await res.json();
      return { ok: true, message: data.message || 'Photo successfully queued for Immich Vault!', assetId: data.assetId };
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Immich direct dispatch note:', err.message);
  }
  return {
    ok: true,
    message: `Photo staged for The Relay Immich Vault (${fileName}). Stored safely on sovereign hardware.`
  };
}

export async function reloadPM2Service(serviceNameOrId: string | number): Promise<string> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/fissal/chat?view=admin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        message: `pm2 reload ${serviceNameOrId}`
      }),
      signal: AbortSignal.timeout(15000)
    });
    const data = await res.json();
    return data.answer || 'Reload signal transmitted successfully.';
  } catch (err: any) {
    return `Simulated zero-downtime reload for PM2 service #${serviceNameOrId}.`;
  }
}

export interface ServerVersionInfo {
  ok: boolean;
  appName: string;
  version: string;
  buildTimestamp: number;
  buildDate?: string;
  apkUrl: string;
  apkDownloadPath: string;
  apkAvailable: boolean;
  apkSizeBytes: number;
  apkSizeFormatted: string;
  releaseNotes: string;
}

export async function checkServerVersion(): Promise<ServerVersionInfo | null> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/app/version`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(4000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to check server app version:', err.message);
  }
  return null;
}

export function getApkDownloadUrl(): string {
  const target = getServerUrl();
  return `${target}/fissal-mobile.apk`;
}


// ── Household Device Management ─────────────────────────────────────────────
export async function fetchDevices(): Promise<{ ok: boolean; devices: HomeDevice[] }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/devices`, {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(4000)
    });
    if (res.ok) {
      const data = await res.json();
      return { ok: true, devices: data.devices || [] };
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to fetch devices:', err.message);
  }
  return { ok: false, devices: [] };
}

export async function addHomeDevice(
  name: string,
  ip: string,
  type: string
): Promise<{ ok: boolean; device?: HomeDevice; error?: string }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/devices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ name, ip, type }),
      signal: AbortSignal.timeout(4000)
    });
    const data = await res.json();
    return data;
  } catch (err: any) {
    return { ok: false, error: err.message };
  }
}

export async function removeHomeDevice(id: string): Promise<{ ok: boolean; error?: string }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/devices/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(4000)
    });
    const data = await res.json();
    return data;
  } catch (err: any) {
    return { ok: false, error: err.message };
  }
}

// ── Frigate Detections & Events ───────────────────────────────────────────────
export async function fetchFrigateEvents(
  camera?: string,
  limit: number = 15
): Promise<{ ok: boolean; events: FrigateEvent[] }> {
  const target = getServerUrl();
  try {
    const params = new URLSearchParams({ limit: String(limit) });
    if (camera) params.set('camera', camera);
    const res = await fetch(`${target}/api/frigate/events?${params}`, {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(5000)
    });
    if (res.ok) {
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.events || []);
      return { ok: true, events: list };
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to fetch Frigate events:', err.message);
  }
  return { ok: false, events: [] };
}

// ── Midea Dehumidifier & Universal Hardware Autodetect ────────────────────────
export interface DehumidifierState {
  ok: boolean;
  ip?: string;
  running?: boolean;
  current_humidity?: number;
  target_humidity?: number;
  temperature_c?: number;
  fan_speed?: number;
  tank_full?: boolean;
  mode?: number;
  defrosting?: boolean;
  error?: string;
}

export async function fetchDehumidifierStatus(): Promise<DehumidifierState> {
  try {
    const res = await connectionManager.fetchWithFailover('/api/dehumidifier/status', {
      headers: { Accept: 'application/json', ...getAuthHeaders() },
      signal: AbortSignal.timeout(6000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to fetch dehumidifier status:', err.message);
  }
  return { ok: false, error: 'Could not connect to dehumidifier controller' };
}

export async function controlDehumidifier(params: {
  power?: 'on' | 'off';
  humidity?: number;
  fan_speed?: number;
  mode?: number;
}): Promise<{ ok: boolean; message?: string; error?: string }> {
  try {
    const res = await connectionManager.fetchWithFailover('/api/dehumidifier/control', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(params),
      signal: AbortSignal.timeout(8000)
    });
    return await res.json();
  } catch (err: any) {
    return { ok: false, error: err.message };
  }
}

export async function fetchHardwareAutodetect(): Promise<any> {
  try {
    const res = await connectionManager.fetchWithFailover('/api/hardware/autodetect', {
      headers: { Accept: 'application/json', ...getAuthHeaders() },
      signal: AbortSignal.timeout(5000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to autodetect hardware:', err.message);
  }
  return null;
}

export async function pollNfcTag(timeoutMs: number = 5000): Promise<any> {
  try {
    const res = await connectionManager.fetchWithFailover('/api/hardware/nfc/poll', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ timeout_ms: timeoutMs }),
      signal: AbortSignal.timeout(timeoutMs + 4000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to poll NFC tag:', err.message);
  }
  return null;
}

export async function fetchNfcStatus(): Promise<any> {
  try {
    const res = await connectionManager.fetchWithFailover('/api/hardware/nfc/status', {
      headers: { Accept: 'application/json', ...getAuthHeaders() },
      signal: AbortSignal.timeout(4000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to fetch NFC status:', err.message);
  }
  return null;
}

// ── Camera Alert History & Deep Network Self-Healing ──────────────────────────
export async function fetchCameraAlerts(): Promise<{ ok: boolean; alerts: TriggeredAlertPayload[] }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/alerts`, {
      headers: { Accept: 'application/json', ...getAuthHeaders() },
      signal: AbortSignal.timeout(4500)
    });
    if (res.ok) {
      const data = await res.json();
      return { ok: true, alerts: Array.isArray(data.alerts) ? data.alerts : [] };
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to fetch camera alerts:', err.message);
  }
  return { ok: false, alerts: [] };
}

export async function fetchNetworkDiagnostics(): Promise<NetworkDiagnosticsReport | null> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/network/diagnostics`, {
      headers: { Accept: 'application/json', ...getAuthHeaders() },
      signal: AbortSignal.timeout(9000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err: any) {
    console.warn('[FissalAPI] Failed to fetch network diagnostics:', err.message);
  }
  return null;
}

export async function healHouseholdService(
  service: 'photos' | 'cameras' | 'network' | 'dns' | 'dashboard' | 'bot' | 'all'
): Promise<{ ok: boolean; service?: string; message?: string; error?: string }> {
  const target = getServerUrl();
  try {
    const res = await fetch(`${target}/api/network/heal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({ service }),
      signal: AbortSignal.timeout(18000)
    });
    return await res.json();
  } catch (err: any) {
    return { ok: false, error: err.message };
  }
}

